/**
 * RunTree — ADR-177 D3/D4, a run's phases, attempts and artifacts.
 *
 * ONE RENDERER, TWO MOUNTS: the Process tab in the editor drawer, and the full-height run
 * page. It is here rather than in either of them because the drawer had its OWN artifact
 * renderer and it only ever drew claims and tests — so an operator who advanced a run to
 * plan watched the rail report "plan → design passed" and show no plan, while the same
 * run's page showed all four steps with their traces. Reported 2026-09-02: "I don't see
 * the plan on the fleet editor Process page. I have to open the /runs page to see it."
 *
 * Two renderers for one object is how a surface acquires a second, worse account of
 * itself. There is now one.
 *
 * THREE RULES IT KEEPS, each a defect this product has already shipped:
 *
 *  - A transcript is ordered by utterance; a run is a tree with retries. A refused
 *    attempt and the successful one that followed are SIBLINGS, both readable.
 *  - Nothing renders a state it did not read: an unmeasured gate says so rather than
 *    rendering as a pass, and a refusal without its reason is a transcript, not a verdict.
 *  - An artifact renders as itself — claims with citations, plan steps with the claim they
 *    trace to, tests with their red-before-green state. The JSON block is a parser
 *    contract and never reaches a person.
 */
import { FileInspector, parseCitation } from './FileInspector'
import { useState } from 'react'
import { verdictClass } from '../utils/verdictClass'

import {
  visiblePhases, samePhase, measurementProgress,
  type AuthoringRunState, type Phase, type TransitionVerdict, type TurnFacts,
} from './AuthoringRail'

/**
 * The content this run authored for a path, if it authored any.
 *
 * READS BOTH SPELLINGS OF THE KEY, and it is not defensiveness. The Go `AuthoredFile`
 * carries no json tags, so the changeset serialises as `Path`/`Content` with capitals.
 * Guessing the lowercase form returns undefined and every authored file renders as a new,
 * empty one — silently, and looking exactly like a run that authored nothing.
 */
export function changesetPaths(run: AuthoringRunState): string[] {
  const out: string[] = []
  for (const f of run.changeset || []) {
    const p = f.Path ?? f.path
    if (p) out.push(p)
  }
  return out
}

export function authoredContent(run: AuthoringRunState, path: string): string | undefined {
  for (const f of run.changeset || []) {
    const p = f.Path ?? f.path
    if (p === path) return f.Content ?? f.content ?? ''
  }
  return undefined
}

/**
 * Whether the run's changeset removes `path` rather than writing it.
 *
 * SAME DUAL-SPELLING READ as changesetPaths/authoredContent, for the same reason: the Go
 * `AuthoredFile.Deleted` carries no json tag either, so it serialises as `Deleted`.
 */
export function authoredIsDeleted(run: AuthoringRunState, path: string): boolean {
  for (const f of run.changeset || []) {
    const p = f.Path ?? f.path
    if (p === path) return Boolean(f.Deleted ?? f.deleted)
  }
  return false
}

/** A phase's label, as an operator reads it rather than as the engine spells it. */
function phaseLabel(p: Phase): string {
  return String(p).replace(/[-_]/g, ' ')
}

/** "1m 4s" / "19s" / "—". Never "0s" for something that was not timed: a duration
 *  nobody measured must not render as an instantaneous one. */
export function duration(seconds?: number): string {
  if (typeof seconds !== 'number' || seconds <= 0) return '—'
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s === 0 ? `${m}m` : `${m}m ${s}s`
}

/** Elapsed between two ISO stamps, in seconds; undefined when either is missing —
 *  which `duration` then renders as "—" rather than as zero. */
export function elapsedS(from?: string, to?: string): number | undefined {
  if (!from || !to) return undefined
  const a = Date.parse(from)
  const b = Date.parse(to)
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return undefined
  return Math.round((b - a) / 1000)
}

function pairByPositionLegacy(turns: TurnFacts[], verdicts: TransitionVerdict[]): (TransitionVerdict | undefined)[] {
  return turns.map((_, index) => verdicts[index])
}

/** Pair a phase's attempts to their verdicts while preserving the positional legacy prefix. */
export function attemptsAt(run: AuthoringRunState, phase: Phase): {
  turn: TurnFacts; verdict?: TransitionVerdict; n: number; legacyPairing: boolean
}[] {
  const allTurns = run.turns || []
  const allVerdicts = run.verdicts || []
  // Older run records have neither identity fields nor prefix counts. Treat those whole
  // records as legacy so their historical positional pairing remains readable.
  const hasTurnSeq = allTurns.some(turn => typeof turn.turn_seq === 'number' && turn.turn_seq > 0)
  const hasVerdictSeq = allVerdicts.some(verdict => typeof verdict.turn_seq === 'number' && verdict.turn_seq > 0)
  const whollyLegacy = !hasTurnSeq && !hasVerdictSeq &&
    !(run.legacy_turns || 0) && !(run.legacy_verdicts || 0)
  const legacyTurnCount = whollyLegacy ? allTurns.length : Math.max(0, run.legacy_turns || 0)
  const legacyVerdictCount = whollyLegacy ? allVerdicts.length : Math.max(0, run.legacy_verdicts || 0)
  const turns = allTurns.filter(turn => samePhase(turn.phase, phase))
  const verdicts = allVerdicts.filter(verdict => samePhase(verdict.from, phase))
  const legacyTurns = allTurns.slice(0, legacyTurnCount).filter(turn => samePhase(turn.phase, phase))
  const legacyVerdicts = allVerdicts.slice(0, legacyVerdictCount).filter(verdict => samePhase(verdict.from, phase))
  const legacyPairs = pairByPositionLegacy(legacyTurns, legacyVerdicts)
  // `n` is the index WITHIN THIS PHASE, deliberately not `turn.attempt`.
  //
  // That field looks like the answer and is not: the engine sets it to the run-wide turn
  // number, so a clean six-phase run carries 1..6 and the tree labelled `design`
  // "attempt 3" on its first and only try. Observed on spartan 2026-09-02, on a run with
  // zero refusals — a retry count invented out of a turn counter.
  return turns.map((turn, i) => {
    const legacyIndex = legacyTurns.indexOf(turn)
    if (legacyIndex >= 0) {
      return { turn, verdict: legacyPairs[legacyIndex], n: i + 1, legacyPairing: true }
    }
    const seq = turn.turn_seq
    const verdict = typeof seq === 'number' && seq > 0
      ? verdicts.slice().reverse().find(candidate => candidate.turn_seq === seq)
      : undefined
    return { turn, verdict, n: i + 1, legacyPairing: false }
  })
}

function VerdictChip({ v }: { v?: TransitionVerdict }) {
  if (!v) {
    // NOT a failure and not a pass. The gate has not spoken about this attempt.
    return <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">not judged</span>
  }
  const cls = verdictClass(v)
  return cls === 'pass'
    ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-800">passed</span>
    : cls === 'wait'
      ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">waiting</span>
      : <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-800">REFUSED</span>
}

/** The artifacts a phase produced, rendered as artifacts (D4). */
function PhaseArtifacts({ run, phase }: { run: AuthoringRunState; phase: Phase }) {
  const a = run.artifacts || {}
  if (samePhase(phase, 'analysis')) {
    const claims = a.claims || []
    if (claims.length === 0) return null
    const cited = claims.filter(c => c.citation).length
    return (
      <ArtifactBox title={`Claims — ${cited} of ${claims.length} cited`}>
        {claims.map((c, i) => (
          <div key={i} className="py-0.5">
            <span className="text-gray-800">{c.text}</span>{' '}
            {c.citation
              ? <span className="font-mono text-[10px] text-emerald-700">{c.citation}</span>
              : <span className="text-[10px] text-red-700">
                uncited{c.used_as_constraint ? ' — and used as a constraint' : ''}
              </span>}
            {/* A CITATION NOBODY CAN FOLLOW IS AN ASSERTION. ADR-166 D5 makes the
                citation the difference between a constraint and a bare claim, so it
                opens — at the cited line, not at the top of the file. */}
            {c.citation && <FileInspector {...parseCitation(c.citation)} />}
            {c.blocking && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-900">blocking</span>}
          </div>
        ))}
      </ArtifactBox>
    )
  }
  if (samePhase(phase, 'plan')) {
    const plan = a.plan || []
    if (plan.length === 0) return null
    return (
      <ArtifactBox title={`Plan — ${plan.length} step${plan.length === 1 ? '' : 's'}`}>
        {plan.map((s, i) => (
          <div key={i} className="py-0.5">
            <span className="text-gray-800">{s.summary}</span>{' '}
            {/* THE TRACE IS THE POINT of the plan gate, so it is never hidden behind an
                expander: a step with no claim behind it is what D3 refuses, and a reader
                must be able to see which one that was. */}
            {s.traces_to
              ? <span className="font-mono text-[10px] text-emerald-700">→ {s.traces_to}</span>
              : <span className="text-[10px] text-red-700">traces to no claim</span>}
            {(s.files || []).map((f, k) => (
              <div key={k} className="text-[10px]">
                <span className="font-mono text-gray-500">{f}</span>
                <FileInspector path={f} />
              </div>
            ))}
          </div>
        ))}
      </ArtifactBox>
    )
  }
  if (samePhase(phase, 'design')) {
    const design = a.design || []
    if (design.length === 0) return null
    return (
      <ArtifactBox title={`Design — ${design.length} file${design.length === 1 ? '' : 's'}`}>
        {design.map((d, i) => (
          <div key={i} className="py-0.5">
            <span className="font-mono text-gray-800">{d.file}</span>
            {(d.symbols || []).length > 0 && (
              <span className="text-[10px] text-gray-600"> · {(d.symbols || []).join(', ')}</span>
            )}
            {(d.unresolved_symbols || []).length > 0 && (
              <div className="text-[10px] text-red-700">
                unresolved: {(d.unresolved_symbols || []).join(', ')}
              </div>
            )}
            {/* Design NAMES files and symbols and authors nothing, so this reads the
                published file: "the output of Run design can't be seen, it just
                references it". The reference now opens. */}
            <FileInspector path={d.file} />
          </div>
        ))}
      </ArtifactBox>
    )
  }
  if (samePhase(phase, 'test-authoring')) {
    const tests = a.tests || []
    if (tests.length === 0) return null
    const red = tests.filter(t => t.fails_against_current_tree).length
    return (
      <ArtifactBox title={`Tests — ${red} of ${tests.length} failing as required`}>
        {tests.map((t, i) => (
          <div key={i} className="py-0.5">
            <span className="font-mono text-gray-800">{t.path}</span>{' '}
            {/* THREE states, not two. This distinction is not decorative: the gate that
                reads it was a silent no-op on production for four consecutive runs
                because "we could not measure it" was being stored as "it is fine". */}
            {t.fails_against_current_tree
              ? <span className="text-[10px] text-emerald-700">red before green ✓</span>
              : t.inconclusive
                ? <span className="text-[10px] text-amber-700">not checked</span>
                : <span className="text-[10px] text-red-700">passes without the change</span>}
            <FileInspector path={t.path} authored={authoredContent(run, t.path)} />
          </div>
        ))}
      </ArtifactBox>
    )
  }
  if (samePhase(phase, 'implementation')) {
    // THE CHANGESET, NOT `changed_files`.
    //
    // `artifacts.changed_files` is the LAST TURN'S files, not the run's. On the first
    // operator run driven to `done` (spartan, 2026-09-02) the results turn authored
    // nothing, `omitempty` dropped the field, and the implementation node rendered
    // nothing at all for a run that had just shipped a merged PR — while
    // `run.changeset` held both files with their contents (a 4,219-char test and a
    // 23,051-char activity).
    //
    // Same family as the attempt counter: a PER-TURN field read as a run-level one. The
    // changeset accumulates across phases and carries content, so it is both the correct
    // source and the one the diff needs.
    const files = changesetPaths(run).length > 0 ? changesetPaths(run) : (a.changed_files || [])
    if (files.length === 0) return null
    // THE RUN'S CHANGESET, not this phase's edit. `changed_files` accumulates from the
    // first turn that authored anything — the tests written at test_authoring are in it
    // too — so titling it "Changed" under implementation claimed authorship this phase
    // does not have. The tests are marked rather than filtered: they ARE part of what
    // gets proposed, and hiding them would understate the change.
    const testPaths = new Set((a.tests || []).map(x => x.path))
    return (
      <ArtifactBox title={`The run's changeset — ${files.length} file${files.length === 1 ? '' : 's'}`}>
        {files.map((f, i) => (
          <div key={i} className="py-0.5">
            <span className="font-mono text-gray-800">{f}</span>
            {authoredIsDeleted(run, f) && (
              <span className="ml-1 text-[10px] text-red-700">deleted</span>
            )}
            {testPaths.has(f) && (
              <span className="ml-1 text-[10px] text-gray-500">authored at test-authoring</span>
            )}
            <FileInspector path={f} authored={authoredContent(run, f)} />
          </div>
        ))}
      </ArtifactBox>
    )
  }
  if (samePhase(phase, 'results')) {
    const a2 = a
    // A MEASUREMENT HAS THREE STATES, AND A TERNARY CAN ONLY SHOW TWO.
    //
    // `revert_proves_red` is `boolean | undefined`, and this card rendered it with a
    // ternary — so `undefined`, which means NOBODY HAS MEASURED IT, fell into the else
    // branch and read "the revert check did not prove red": a verdict about the operator's
    // change, printed for a measurement that never reported.
    //
    // REPORTED BY THE OPERATOR on spartan 20260917T174002Z-1a0d63, whose record held
    // `revert_proves_red: null` beside a `measuring` block showing the work COMPLETE — one
    // file proven, the activity skipped as documentation-only, eight seconds. The turn died
    // before writing the verdict, and the card turned that into a failing check.
    //
    // This is the class the backend spent 2026-09-16/17 removing — an ERRORED test is not a
    // red test, an unmeasured test is not a passing one — sitting in the view layer, where
    // it is the only version an operator ever sees.
    const progress = measurementProgress(run)
    return (
      <ArtifactBox title="Results">
        <div className="py-0.5">
          {a2.tests_now_pass
            ? <span className="text-emerald-700">the authored tests pass against the change</span>
            : <span className="text-amber-700">the authored tests were not measured as passing</span>}
        </div>
        <div className="py-0.5">
          {a2.revert_proves_red === true
            ? <span className="text-emerald-700">reverting the change turns them red again</span>
            : a2.revert_not_applicable === true
              // #3173: documentation-only changes have nothing to revert, and amber beside
              // a passed results gate would falsely say the change failed to prove red.
              ? <span className="text-gray-700">the revert check does not apply: the only non-test change is an activity's description or version</span>
              : a2.revert_proves_red === false
                ? <span className="text-amber-700">the revert check did not prove red</span>
                : <span className="text-gray-600">the revert check is not measured yet</span>}
        </div>
        {/* THE COUNT, IN THE CARD — not only in the pacing control.
            "there is no visual cue of any progress" was the operator's other sentence about
            this run, and the cause was placement: measurementProgress() rendered above the
            phase list while the Results card, the one surface a person reads WHILE waiting,
            showed only a conclusion. A count belongs beside the thing it counts. */}
        {progress && (
          <div className="py-0.5 text-[11px] text-sky-800">{progress}</div>
        )}
        {a2.measurement_detail && (
          <div className="py-0.5 text-[10px] text-gray-600">{a2.measurement_detail}</div>
        )}
      </ArtifactBox>
    )
  }
  return null
}

function ArtifactBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-1 rounded border border-gray-200 bg-white px-2 py-1 text-[11px]">
      <div className="font-medium text-gray-700">{title}</div>
      {children}
    </div>
  )
}

function PhaseNode({ run, phase, current, onRedrive }: {
  run: AuthoringRunState
  phase: Phase
  current: Phase
  onRedrive?: (phase: Phase, note: string) => Promise<void>
}) {
  const attempts = attemptsAt(run, phase)
  const order = visiblePhases(run.scope?.collapse_phases ?? false)
  const pi = order.findIndex(p => samePhase(p, phase))
  const reached = pi >= 0 && pi <= order.findIndex(p => samePhase(p, current))
  const refusals = attempts.filter(a => a.verdict && verdictClass(a.verdict) === 'refusal').length
  const latestRefusal = attempts.slice().reverse().find(a => a.verdict && verdictClass(a.verdict) === 'refusal')
  const total = attempts.reduce((n, a) => n + (a.turn.duration_s || 0), 0)

  // #2530's six refusals unrolled six phases; every phase starts collapsed so the run's
  // shape stays readable, and the summary names the latest refusal for one-click detail.
  const [open, setOpen] = useState(false)

  const summary = (
    <>
      <span className="text-[10px] text-gray-500">total {duration(total)}</span>
        {/* THE RETRY COUNT IS ON THE NODE, not buried in the attempts. It is the single
            most informative number about a phase and it has never been visible. */}
        {(attempts.length > 1 || refusals > 0) && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-900">
            {attempts.length} attempt{attempts.length === 1 ? '' : 's'}{refusals > 0 && (
              <> · {refusals} refused<span className="ml-1 font-mono">
                [{latestRefusal?.verdict?.code || 'no code recorded'}]
              </span></>
            )}
          </span>
        )}
      {attempts.length === 0 && (
        <span className="text-[10px] text-gray-400">
          {reached ? 'no turn recorded' : 'not reached'}
        </span>
      )}
    </>
  )

  return (
    <div className="border-l-2 border-gray-200 pl-3">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-baseline gap-2 py-0.5 text-left hover:bg-gray-50">
        <span className="text-[10px] text-gray-400">{open ? '▾' : '▸'}</span>
        <span className={`text-[12px] font-semibold ${reached ? 'text-gray-900' : 'text-gray-400'}`}>
          {phaseLabel(phase)}
        </span>
        {summary}
      </button>

      {!open && <div className="sr-only">{phaseLabel(phase)} is collapsed</div>}

      {open && (<>
      {attempts.map(({ turn, verdict, n, legacyPairing }, i) => (
        <div key={i} className="mt-1 rounded border border-gray-200 bg-gray-50 px-2 py-1">
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="text-gray-700">attempt {n}</span>
            {turn.error
              ? <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-800">failed</span>
              : <VerdictChip v={verdict} />}
            <span className="text-[10px] text-gray-500">{duration(turn.duration_s)}</span>
            {legacyPairing && <span className="text-[10px] text-gray-500">paired by order (older record)</span>}
            {turn.model && <span className="text-[10px] text-gray-400">{turn.model}</span>}
            {/* ADR-199 D3/D7 — WHO AUTHORED, and what the session cost. An agent-authored
                attempt is not the same mechanism as a completer one, so a reader comparing
                attempts must be able to see which is which without opening the record. */}
            {turn.agent && (
              <span className="text-[10px] text-indigo-700" data-testid="agent-session">
                {turn.agent.engine.replace(/^agent:/, '')}
                {` · ${turn.agent.num_turns} agent turns`}
                {turn.agent.rail_checks > 0 && ` · ${turn.agent.rail_checks} rail-check`}
                {turn.agent.cost_usd > 0 && ` · $${turn.agent.cost_usd.toFixed(2)}`}
                {turn.agent.resumed && ' · resumed'}
              </span>
            )}
          </div>
          {turn.error && <div className="mt-0.5 text-[11px] text-red-800">{turn.error}</div>}
          {turn.agent && (turn.agent.dropped_paths?.length ?? 0) > 0 && (
            <div className="mt-0.5 text-[11px] text-amber-800">
              not proposable, so not in the change: {turn.agent.dropped_paths!.join(', ')}
            </div>
          )}
          {/* A REFUSAL WITHOUT ITS REASON IS A TRANSCRIPT. Rendered unconditionally for a
              refusal, including the case where the reason is missing — because "no reason
              recorded" is itself the finding, and hiding the row would hide it. Waits are
              shown separately in amber, with no invented missing-reason finding. */}
          {verdict && verdictClass(verdict) === 'refusal' && (
            <div className="mt-0.5 text-[11px] text-red-800">
              {verdict.reason || 'no reason recorded'}
              {verdict.code && <span className="ml-1 font-mono text-[10px] text-red-600">[{verdict.code}]</span>}
            </div>
          )}
          {verdict && verdictClass(verdict) === 'wait' && verdict.reason && (
            <div className="mt-0.5 text-[11px] text-amber-800">
              {verdict.reason}
              {verdict.code && <span className="ml-1 font-mono text-[10px] text-amber-600">[{verdict.code}]</span>}
            </div>
          )}
          {verdict && verdict.passed && verdict.reason && (
            <div className="mt-0.5 text-[11px] text-emerald-800">{verdict.reason}</div>
          )}
        </div>
      ))}

      {/* ARTIFACTS ONLY WHERE A TURN ACTUALLY RAN.
          `reached` is derived from the phase ORDER, so it is true for the phase the run
          is parked at — a phase that has not run. PhaseArtifacts then read run-level
          cumulative fields and drew them under it, and the implementation node rendered
          "no turn recorded" and "Changed — 1 file" in the same box, over a file authored
          two phases earlier at test_authoring. Reported 2026-09-02: "I have not run
          implementation yet but I am seeing a file changed on /runs for implementation."
          A node that reports work it did not do is worse than one that reports nothing. */}
      {attempts.length > 0 && <PhaseArtifacts run={run} phase={phase} />}

      {/* ADR-177 — ask for this phase again, with a reason.
          Offered only where a turn actually ran: a phase that produced nothing has
          nothing to reconsider, and rewinding to it would put the run in a state it was
          never in. Absent entirely for a reader — onRedrive is only passed in edit
          mode. */}
      {onRedrive && attempts.length > 0 && (
        <RedriveBox phase={phase} onRedrive={onRedrive} />
      )}
      </>)}
    </div>
  )
}

/**
 * The ask: what should change, and why.
 *
 * A NOTE IS REQUIRED, and the button says so rather than the server refusing after the
 * click. Without one the turn is asked the same question that produced the answer being
 * rejected — the same reasoning that makes an empty checkpoint redirect a continue (D6).
 */
function RedriveBox({ phase, onRedrive }: {
  phase: Phase
  onRedrive: (phase: Phase, note: string) => Promise<void>
}) {
  const [openBox, setOpenBox] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  if (!openBox) {
    return (
      <button type="button" onClick={() => setOpenBox(true)}
        className="mt-1 text-[10px] text-indigo-700 hover:underline">
        Ask for this phase again…
      </button>
    )
  }
  return (
    <div className="mt-1 rounded border border-indigo-200 bg-indigo-50 p-2 space-y-1">
      <div className="text-[10px] text-indigo-900">
        What should change about {phaseLabel(phase)}? The next turn gets this note and
        everything the phase already produced.
      </div>
      <textarea
        value={note} onChange={e => setNote(e.target.value)} rows={2}
        placeholder="e.g. step 2 rewrites the wrong branch — deliver, not collect"
        className="w-full rounded border border-gray-300 px-2 py-1 text-[11px]"
      />
      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button" disabled={busy || note.trim() === ''}
          title={note.trim() === ''
            ? 'Say what should change first — without it the turn is asked the same question that produced this answer.'
            : `Rewind to ${phaseLabel(phase)} and run it again with this note.`}
          onClick={async () => {
            setBusy(true); setErr('')
            try {
              await onRedrive(phase, note.trim())
              setOpenBox(false); setNote('')
            } catch (e) {
              setErr(e instanceof Error ? e.message : 'the request could not be recorded')
            } finally { setBusy(false) }
          }}
          className="rounded bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          {busy ? 'Rewinding…' : `Redo ${phaseLabel(phase)}`}
        </button>
        <button type="button" disabled={busy} onClick={() => { setOpenBox(false); setErr('') }}
          className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
      </div>
      {err && <div className="text-[10px] text-red-700">{err}</div>}
    </div>
  )
}


/** The whole tree: every phase this run will pass through, in order. */
export function RunTree({ run, onRedrive }: {
  run: AuthoringRunState
  /** Present only in edit mode. Absent means the tree offers no way to change anything,
   *  which is what makes it safe to show to a reader. */
  onRedrive?: (phase: Phase, note: string) => Promise<void>
}) {
  const order = visiblePhases(run.scope?.collapse_phases ?? false)
  return (
    <div className="space-y-2">
      {order.map(p => (
        <PhaseNode key={p} run={run} phase={p} current={run.phase} onRedrive={onRedrive} />
      ))}
    </div>
  )
}
